import React, { useMemo, useState } from 'react';
import { useEvent, useUnit } from 'effector-react';
import { Button, ButtonSize, ButtonType } from '@nx-frontend/shared/ui/button';
import { Checkbox, CheckboxSize, CheckboxType } from '@nx-frontend/shared/ui/checkbox';
import { UiInput, UiInputView } from '@nx-frontend/shared/ui/input';
import { closeModalEvent } from '@nx-frontend/shared/ui/modal';
import {
  useNavigateMenuItem,
  usePortalNoteCreate,
  usePageCreate,
  useProcessCreate,
  useSectionCreate,
} from '@nx-frontend/shared/hooks';
import { duplicateMenuItemFx, getMenusExistedSlugsFx } from '@nx-frontend/shared/store/feature-menu-tree';
import { $portalId } from '@nx-frontend/shared/store/portal-app';
import { MenuItem, MenuType, NoteCreateTileType } from '@nx-frontend/shared/types';
import { getUniqueUrl } from '@nx-frontend/shared/utils';
import { CreateEntityTab, CreateEntityTabs } from './create-entity-tabs';
import { EntityTemplateSelect } from './entity-template-select';
import { OriginSelect } from './origin-select';

export enum CreatePortalEntityType {
  FOLDER = 'folder',
  PAGE = 'page',
  PROCESS = 'process',
}

export function CreatePortalEntity({
  entityType = CreatePortalEntityType.PAGE,
  menuType,
  parentId,
}: {
  entityType: CreatePortalEntityType;
  menuType: MenuType;
  parentId?: string;
}) {
  const [portalId] = useUnit([$portalId]);
  const [activeTab, setActiveTab] = useState<CreateEntityTab>(CreateEntityTab.BLANK);
  const [title, setTitle] = useState('');
  const [templateId, setTemplateId] = useState<NoteCreateTileType | null>(null);
  const [visible, setVisible] = useState(true);
  const [closeModal, duplicateMenuItem, getMenusExistedSlugs] = useEvent([
    closeModalEvent,
    duplicateMenuItemFx,
    getMenusExistedSlugsFx,
  ]);
  const [projectionMenuItem, setProjectionMenuItem] = useState<MenuItem | null>();

  const pageCreate = usePageCreate();
  const noteCreate = usePortalNoteCreate();
  const sectionCreate = useSectionCreate();
  const processCreate = useProcessCreate();
  const navigateMenuItem = useNavigateMenuItem();

  const handleChangeTab = (tab: CreateEntityTab) => {
    setActiveTab(tab);
  };

  const submitDisabled = useMemo(() => {
    if (activeTab === CreateEntityTab.BLANK) {
      return !title.trim().length;
    } else if (activeTab === CreateEntityTab.TEMPLATE) {
      return !templateId;
    } else if (activeTab === CreateEntityTab.PROJECTION) {
      return !projectionMenuItem;
    }
  }, [activeTab, projectionMenuItem, templateId, title]);

  const submit = async () => {
    const preparedTitle = activeTab === CreateEntityTab.BLANK ? title.trim() : undefined;

    if (activeTab === CreateEntityTab.BLANK) {
      if (entityType === CreatePortalEntityType.PAGE) {
        await pageCreate(
          {
            title: preparedTitle,
            openAfterCreate: true,
            skipCreateNoteId: true,
          },
          { parentId: parentId, menuType: menuType, visible }
        );
      } else if (entityType === CreatePortalEntityType.FOLDER) {
        await sectionCreate({ title, openAfterCreate: true }, { parentId: parentId, menuType: menuType, visible });
      } else if (entityType === CreatePortalEntityType.PROCESS) {
        await processCreate(
          { title, openAfterCreate: true, items: [] },
          { parentId: parentId, menuType: menuType, visible }
        );
      }
    } else if (activeTab === CreateEntityTab.TEMPLATE) {
      if (entityType === CreatePortalEntityType.PAGE) {
        if (templateId) {
          await noteCreate(
            {
              type: templateId,
              title: preparedTitle,
            },
            { parentId: parentId, menuType: menuType, visible }
          );
        }
      }
    } else if (activeTab === CreateEntityTab.PROJECTION) {
      if (projectionMenuItem) {
        if (!projectionMenuItem) {
          return;
        }

        await duplicateMenuItem({
          portalId,
          srcId: projectionMenuItem.id,
          newName: projectionMenuItem.name,
          menuType,
          parentMenuItemId: parentId,
          isSyncedCopy: true,
        });
        const existedSlugs = await getMenusExistedSlugs([
          MenuType.PORTAL_SIDEBAR,
          MenuType.PORTAL_TOPBAR,
          MenuType.PORTAL_FOOTER,
        ]);
        const slug = getUniqueUrl(projectionMenuItem.slug, existedSlugs);
        const lastSlashIndex = projectionMenuItem.url.lastIndexOf('/');
        let path = projectionMenuItem.url;
        if (lastSlashIndex !== -1) {
          path = projectionMenuItem.url.slice(0, lastSlashIndex + 1) + slug;
        }
        setTimeout(() => {
          navigateMenuItem({ path });
        }, 1000);
      }
    }

    closeModal();
  };

  const titleLabel = useMemo(() => {
    if (entityType === CreatePortalEntityType.PAGE) {
      return 'Page name';
    } else if (entityType === CreatePortalEntityType.FOLDER) {
      return 'Folder name';
    } else if (entityType === CreatePortalEntityType.PROCESS) {
      return 'Process name';
    }
    return '';
  }, [entityType]);

  const handleChangeProjectionMenuItem = (value: MenuItem | null) => {
    setProjectionMenuItem(value);
    if (value) {
      setVisible(value.visible);
    }
  };

  return (
    <div className="flex flex-col gap-4 min-w-96">
      <CreateEntityTabs type={entityType} activeTab={activeTab} hasParent={!!parentId} changeTab={handleChangeTab} />

      {activeTab === CreateEntityTab.BLANK && (
        <UiInput
          label={titleLabel}
          view={UiInputView.LABEL_TOP}
          value={title}
          autoFocus={true}
          placeholder="Type name here"
          onChange={({ target: { value } }) => setTitle(value)}
        />
      )}
      {activeTab === CreateEntityTab.TEMPLATE && (
        <EntityTemplateSelect entityType={entityType} changeTemplateId={setTemplateId} />
      )}
      {activeTab === CreateEntityTab.PROJECTION && (
        <OriginSelect type={entityType} changeProjectionMenuItem={handleChangeProjectionMenuItem} />
      )}

      <div>
        <Checkbox
          id="index"
          label="Visibility"
          checked={visible}
          disabled={activeTab === CreateEntityTab.PROJECTION}
          type={CheckboxType.SWITCH}
          boxSize={CheckboxSize.SM}
          onChange={setVisible}
        />
        {activeTab === CreateEntityTab.PROJECTION && (
          <div className="pl-10 text-[10px] text-neutral-400">Synchronized with the original. Cannot be edited</div>
        )}
      </div>

      <div className="flex gap-5 justify-end">
        <Button type={ButtonType.STROKED} size={ButtonSize.XL} onClick={() => closeModal()}>
          Cancel
        </Button>
        <Button size={ButtonSize.XL} type={ButtonType.FLAT} onClick={submit} disabled={submitDisabled}>
          Add
        </Button>
      </div>
    </div>
  );
}
